import api from "@/utils/api"

// ─────────────────────────────────────────────────────────────────────────────
// Smartflo Dialer Campaign — filtered lead upload
//
// This screen does NOT render a lead grid. The user builds a filter (dates,
// bucket, disposition L1/L2/L3, products), picks a Tata Tele lead list + campaign,
// and pushes every matching lead.
//
// Smartflo model: leads live in a "Lead List" (broadcast list); a dialer campaign
// dials one or more lists. The list + campaign are chosen independently here.
// The push resolves matching CRM leads → phone/name → Smartflo
//   POST /v1/broadcast/leads/{lead_list_id}  { data:[{field_0,field_1}], duplicate_option }
//
// The browser never talks to Smartflo directly (JWT is encrypted in the DB —
// crm_outbound_call_configs, config_id=2). All Tata calls go through our backend.
// Backend endpoints are built after this UI ("UI first").
// ─────────────────────────────────────────────────────────────────────────────

export type DuplicateOption = "skip" | "overwrite" | "clone"

export type CampaignBucket = "abandoned_cart" | "reorder" | "rto" | "edd" | "ndr"

/** Buckets filtered by day-count ageing rather than a calendar event date. */
export const AGEING_BUCKETS: CampaignBucket[] = ["edd", "ndr"]

/**
 * Which disposition tree a bucket uses.
 *
 * EDD and Failed Delivery (NDR) are the same lead_bucket ('edd') split by whether a
 * delivery attempt has genuinely failed, so they share one tree — and /dispositions/tree
 * has no 'ndr' type at all, so asking for it would 400.
 */
export const dispositionBucketFor = (b: CampaignBucket | null): string =>
    b === "ndr" ? "edd" : (b || "abandoned_cart")

/** Filter that selects which CRM leads get pushed. */
export interface CampaignFilters {
    /** Bucket-specific event date (ABC=last ABC, reorder=due date, rto=RTO date). */
    date_from: string | null
    date_to: string | null
    /** null = all buckets */
    bucket: CampaignBucket | null
    /** multi-select disposition option_ids; empty = any */
    l1: number[]
    l2: number[]
    l3: number[]
    /** shopify_products.product_id values; empty = any product */
    product_ids: number[]

    // ── EDD / NDR only: day-count ranges, ignored for other buckets ──────────
    /** Days since the parcel was picked up (Clickpost code 4). */
    dispatch_ageing_min: number | null
    dispatch_ageing_max: number | null
    /**
     * Days past the promised delivery date, positive.
     * The EDD screen's own `edd_ageing` column is days *to* EDD (negative once
     * breached); this is its negation so a campaign reads "overdue 2 to 5 days".
     */
    days_overdue_min: number | null
    days_overdue_max: number | null
}

export const emptyFilters = (): CampaignFilters => ({
    date_from: null,
    date_to: null,
    bucket: null,
    l1: [],
    l2: [],
    l3: [],
    product_ids: [],
    dispatch_ageing_min: null,
    dispatch_ageing_max: null,
    days_overdue_min: null,
    days_overdue_max: null,
})

// ── Reference data ───────────────────────────────────────────────────────────

export interface DispositionOption {
    option_id: number
    value: string
}
export interface DispositionTree {
    l1Options: DispositionOption[]
    l2ByL1: Record<string, DispositionOption[]>
    l3ByL2: Record<string, DispositionOption[]>
}

export interface ProductOption {
    product_id: number
    title: string
    product_type?: string | null
}

export interface CatProduct {
    product_id: number
    name: string
}
export interface ProductCategory {
    cat_id: number
    cat: string
    colour?: string | null
    products: CatProduct[]
}

export interface DialerCampaign {
    campaign_id: number
    name: string
    agent_count: number
}

export interface LeadList {
    list_id: number
    name: string
    description?: string | null
    lead_count?: number | null
}

export interface PushResult {
    pushed: number
    skipped: number
    failed: number
    cleared?: number
    message: string
    /** Per-batch rejection reasons from Smartflo (first 10). */
    errors?: string[]
}

/** A queued push. Smartflo rate-limits bulk uploads, so a push runs in the background. */
export type PushJobStatus = "queued" | "running" | "done" | "partial" | "failed"
export interface PushJob {
    job_id: string
    status: PushJobStatus
    total: number
    pushed: number
    failed: number
    cleared: number
    batches_total: number
    batches_done: number
    /** Live line, e.g. "Sending batch 3 of 6..." or "Smartflo asked us to slow down...". */
    note: string | null
    /** Final summary, once finished. */
    message: string | null
    errors: string[]
    /** Pushes queued ahead of this one (status "queued"). */
    ahead: number
}
export const isJobActive = (j: PushJob | null): boolean => !!j && (j.status === "queued" || j.status === "running")

/** Export is capped lower than a push — a CSV this size stops being a spot-check. */
export const EXPORT_MAX_ROWS = 15000

export interface ExportResult {
    downloadUrl: string
    fileName: string
    recordCount: number
}

// ── API calls ──────────────────────────────────────────────────────────────

/** Bucket-specific disposition tree (L1 → L2 → L3). */
export const cuGetDispositionTree = (bucket: string) =>
    api.req<DispositionTree>({ url: `/dispositions/tree/${bucket}`, method: api.Method.GET })

/** Active Shopify products for the product multi-select. */
export const cuGetProducts = () =>
    api.req<ProductOption[]>({ url: "/shopify/products?status=active", method: api.Method.GET })

/** RR categories with their mapped products (for the category/product tree picker). */
export const cuGetProductTree = () =>
    api.req<ProductCategory[]>({ url: "/rr/product-tree", method: api.Method.GET })

/** Tata Tele lead lists (backend proxies Smartflo GET /v1/broadcast/lists). */
export const cuListLeadLists = () =>
    api.req<LeadList[]>({ url: "/campaign-upload/lead-lists", method: api.Method.GET })

/**
 * Create a new Tata lead list pre-configured with our 12-column layout
 * (phone, name, email, address, company, alt phone, product, ABC date, ABC time,
 * time group, priority, external ref). Name: <=25 chars, letters/digits/spaces only.
 */
export const cuCreateLeadList = (name: string, description?: string) =>
    api.req<{ list_id: number | null; name: string; message: string }>({
        url: "/campaign-upload/create-list", method: api.Method.POST,
        data: { name, description },
    })

/** Tata Tele dialer campaigns (backend proxies Smartflo GET /v1/dialer/campaign). */
export const cuListCampaigns = () =>
    api.req<DialerCampaign[]>({ url: "/campaign-upload/campaigns", method: api.Method.GET })

/** Count CRM leads matching the filter (product match auto-scoped by bucket). */
export const cuCountLeads = (filters: CampaignFilters) =>
    api.req<{ count: number }>({ url: "/campaign-upload/count", method: api.Method.POST, data: filters })

/** Export CSV of leads matching the filter. Refused server-side above EXPORT_MAX_ROWS. */
export const cuExportLeads = (filters: CampaignFilters) =>
    api.req<ExportResult>({ url: "/campaign-upload/export", method: api.Method.POST, data: filters })

/** Queue a push of all matching leads into the chosen lead list; returns the job to poll. */
export const cuPushLeads = (body: {
    filters: CampaignFilters
    lead_list_id: number
    campaign_id: number | null
    duplicate_option: DuplicateOption
    clear_existing: boolean
}) =>
    api.req<PushJob>({ url: "/campaign-upload/push", method: api.Method.POST, data: body })

export const cuPushJobStatus = (jobId: string) =>
    api.req<PushJob>({ url: `/campaign-upload/push-jobs/${jobId}`, method: api.Method.GET })

/** Queued/running pushes — used to rejoin one after a page reload. */
export const cuActivePushJobs = () =>
    api.req<PushJob[]>({ url: "/campaign-upload/push-jobs/active", method: api.Method.GET })

// ── Preview fallbacks (only until the backend endpoints ship) ─────────────────
// Sourced from the 6 live campaigns on Smartflo account 481309 (Jul 2026).

export const PREVIEW_CAMPAIGNS: DialerCampaign[] = [
    { campaign_id: 481717, name: "Not Answered Calls Recurring", agent_count: 52 },
    { campaign_id: 511857, name: "Female Product Campaign — Not Connected Calls", agent_count: 41 },
    { campaign_id: 512113, name: "Male Products Campaign", agent_count: 38 },
    { campaign_id: 512857, name: "Female Not Interested — Purchase Later", agent_count: 33 },
    { campaign_id: 512887, name: "Male Not Interested — Purchase Later", agent_count: 29 },
    { campaign_id: 522289, name: "EDD Calling Campaign", agent_count: 24 },
]

export const PREVIEW_LEAD_LISTS: LeadList[] = [
    { list_id: 0, name: "— sample: connect backend to load lists —", description: null, lead_count: null },
]
